import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Contacts as ContactsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { AxiosResponse } from 'axios';
import { getAdminOrders } from '../../../services/admin-service';
import { Contact } from '../../../types';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Define types that match exactly what the backend returns
interface Reseller {
  id: string;
  name: string;
  email: string;
}

interface Customer {
  company_name: string;
  phone: string;
  email: string;
  cnpj: string;
  sector: string;
  state_registration: string | null;
  id: string;
  reseller_id: string;
  created_at: string;
  reseller: Reseller;
  contacts?: Contact[];
}

interface Quote {
  id: string;
  code: string;
  status: string;
  expiration_date: string;
  reseller_id: string;
  created_at: string;
  reseller: Reseller;
}

interface ShippingAddress {
  street: string;
  number: string | null;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  is_default: boolean;
  complement: string | null;
  is_billing: boolean;
  is_delivery: boolean;
  billing: boolean;
  id: string;
  customer_id: string;
}

interface Order {
  quote_id: string;
  customer_id: string;
  shipping_address_id: string;
  payment_method: string;
  payment_term: string;
  status: string;
  id: string;
  created_at: string;
  quote: Quote;
  customer: Customer;
  shipping_address: ShippingAddress;
  order_items: OrderItem[];
  reseller_name?: string;
}

interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  cisp1_commission: number;
  reseller_commission: number;
  final_price: number | null;
}

interface ApiResponse {
  items: Order[];
  total: number;
}

// Component for expandable row with order details
const OrderRow = ({ order }: { order: Order }) => {
  const [open, setOpen] = useState(false);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const navigate = useNavigate();

  // Debug order data
  console.log('Rendering OrderRow with order:', order);

  const handleOpenContactsModal = () => {
    setOpenContactsModal(true);
  };

  const handleCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'primary';
      case 'processing':
        return 'warning';
      case 'shipped':
        return 'info';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Format currency in Brazilian Real
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Make sure all required properties exist
  if (!order || !order.id) {
    console.error('Invalid order object:', order);
    return null;
  }

  // Order items are now available directly in the order object
  const orderItems = order.order_items || [];

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Link to={`/orders/${order.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            {order.quote?.code || order.id.substring(0, 8)}
          </Link>
        </TableCell>
        <TableCell>{order.customer?.company_name || 'Desconhecido'}</TableCell>
        <TableCell>
          {order.reseller_name || order.customer?.reseller?.name || 'Desconhecido'}
        </TableCell>
        <TableCell>
          <Chip
            label={order.status || 'Desconhecido'}
            size="small"
            color={getStatusChipColor(order.status || '') as any}
          />
        </TableCell>
        <TableCell align="right">
          {/* Calculate total from order items */}
          {formatCurrency(
            orderItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0)
          )}
        </TableCell>
        <TableCell>{formatDate(order.created_at)}</TableCell>
        <TableCell align="right">
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/admin/orders/${order.id}/acer-status`)}
          >
            Status Acer
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Detalhes do Pedido
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {order.quote && order.quote.code && (
                        <>
                          {' '}
                          <strong>Código da Cotação:</strong> {order.quote.code}
                        </>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Método de Pagamento:</strong> {order.payment_method}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Prazo de Pagamento:</strong> {order.payment_term} dias
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Criado em:</strong> {formatDate(order.created_at)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informações de Envio
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {order.shipping_address ? (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Endereço:</strong> {order.shipping_address.street}
                          {order.shipping_address.number
                            ? `, ${order.shipping_address.number}`
                            : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cidade/Estado:</strong> {order.shipping_address.city},{' '}
                          {order.shipping_address.state}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>CEP/País:</strong> {order.shipping_address.zip_code},{' '}
                          {order.shipping_address.country}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informações de envio não disponíveis
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Informações do Cliente
                    </Typography>
                    <Button
                      color="primary"
                      startIcon={<ContactsIcon />}
                      onClick={handleOpenContactsModal}
                      size="small"
                    >
                      Ver Contatos
                    </Button>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    {order.customer ? (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Empresa:</strong> {order.customer.company_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Email:</strong> {order.customer.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Telefone:</strong> {order.customer.phone}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>CNPJ:</strong> {order.customer.cnpj}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Setor:</strong> {order.customer.sector}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informações do cliente não disponíveis
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informações do Revendedor
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {order.customer?.reseller || order.quote?.reseller ? (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Nome:</strong>{' '}
                          {order.customer?.reseller?.name || order.quote?.reseller?.name || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Email:</strong>{' '}
                          {order.customer?.reseller?.email || order.quote?.reseller?.email || 'N/A'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informações do revendedor não disponíveis
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Itens do Pedido
              </Typography>

              {orderItems.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Quantidade</TableCell>
                        <TableCell align="right">Preço Unitário</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItems.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.product_name || 'Produto Desconhecido'}</TableCell>
                          <TableCell>{item.product_sku || 'N/A'}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.quantity * item.unit_price)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Informações de itens não disponíveis
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Contacts Modal */}
      <Dialog
        open={openContactsModal}
        onClose={handleCloseContactsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <ContactsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Contatos de {order?.customer?.company_name || 'Cliente'}
              </Typography>
            </Box>
            <IconButton
              aria-label="fechar"
              onClick={handleCloseContactsModal}
              sx={{
                color: theme => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {order?.customer?.contacts && order.customer.contacts.length > 0 ? (
            <Grid container spacing={2}>
              {order.customer.contacts.map((contact, index) => (
                <Grid item xs={12} sm={6} key={contact.id || index}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      border: contact.is_primary ? '2px solid #80c343' : '1px solid #e0e0e0',
                      position: 'relative',
                    }}
                  >
                    {contact.is_primary && (
                      <Chip
                        label="Principal"
                        size="small"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: 8,
                          backgroundColor: '#80c343',
                        }}
                      />
                    )}
                    <Box display="flex" alignItems="center" mb={1}>
                      <PersonIcon sx={{ mr: 1, color: '#80c343' }} />
                      <Typography variant="h6" fontWeight="medium">
                        {contact.name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {contact.phone}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {contact.role}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              color="text.secondary"
            >
              <ContactsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Nenhum contato encontrado
              </Typography>
              <Typography variant="body2" textAlign="center">
                Este cliente ainda não possui contatos cadastrados.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactsModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AdminOrdersPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Pedidos', path: '/admin/orders' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        // Use the service function instead of direct API call
        const data = await getAdminOrders(page * rowsPerPage, rowsPerPage);
        console.log('API response data:', data);

        // Check if data is valid and contains items array
        if (data && Array.isArray(data.items)) {
          console.log('Items array length:', data.items.length);
          setOrders(data.items as unknown as Order[]);
          setTotalItems(data.total || data.items.length);
        } else {
          console.error('Invalid data format or missing items array:', data);
          setOrders([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        enqueueSnackbar('Falha ao carregar pedidos', { variant: 'error' });
        setOrders([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Debug orders state
  console.log('Current orders state:', orders);
  console.log('Orders array length:', orders?.length || 0);

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px" />
                  <TableCell>Pedido #</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Revendedor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders && orders.length > 0 ? (
                  orders.map(order => <OrderRow key={order.id} order={order} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Nenhum pedido encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default AdminOrdersPage;
